import clsx from 'clsx';
import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';

import IconArrowHorizontal from '@/assets/icons/icon-arrow-horizontal.svg?react';
import IconArrowVertical from '@/assets/icons/icon-arrow-vertical.svg?react';
import IconArrowHorizontalFilled from '@/assets/icons/icon-arrow-horizontal-filled.svg?react';
import IconArrowVerticalFilled from '@/assets/icons/icon-arrow-vertical-filled.svg?react';
import useEditableLayer from '@/hooks/useEditableLayer';

interface ContentBlockLabelProps {
  text?: string;
  align?: 'vertical' | 'horizontal';
  onClick?: (size: 'row' | 'column') => void;
  className?: string;
  switcher?: boolean;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  id: string;
  editTitle: boolean;
  closeEditMode: () => void;
}

const aligns: Array<'vertical' | 'horizontal'> = ['vertical', 'horizontal'];

export const ContentBlockLabel: React.FC<ContentBlockLabelProps> = ({
  align = 'vertical',
  onClick,
  className,
  text = 'Container',
  switcher = true,
  icon: Icon,
  id,
  editTitle,
  closeEditMode,
}) => {
  const [selectedSize, setSelectedSize] = useState<'vertical' | 'horizontal'>(align);
  const [highlightStyle, setHighlightStyle] = useState({ width: 0, left: 0 });
  const [isAnimated, setIsAnimated] = useState(false);
  const { inputTitleRef, textValue, handleOnBlur, handleOnChange, handleOnKeyUp } =
    useEditableLayer({
      id,
      text,
      onExitEditing: () => {
        closeEditMode();
      },
    });
  const optionRefs = useRef<Record<'vertical' | 'horizontal', HTMLButtonElement | null>>({
    vertical: null,
    horizontal: null,
  });

  useEffect(() => {
    setSelectedSize(align);
  }, [align]);

  useLayoutEffect(() => {
    const activeEl = optionRefs.current[selectedSize];
    if (activeEl) {
      setHighlightStyle({
        width: activeEl.offsetWidth,
        left: activeEl.offsetLeft,
      });
    }
  }, [selectedSize, switcher]);

  const handleClick = (newSize: 'vertical' | 'horizontal') => {
    setIsAnimated(true);
    setSelectedSize(newSize);
    if (onClick) onClick(newSize === 'vertical' ? 'column' : 'row');
  };

  useEffect(() => {
    if (editTitle) {
      inputTitleRef.current?.focus();
    }
  }, [editTitle]);

  return (
    <>
      <div
        className={clsx(
          'inline-flex h-7 items-center gap-1 rounded-md bg-[#6245F2] px-2 py-1 shadow-[0px_4px_12px_0px_rgba(0,0,0,0.10)]',
          className,
        )}
      >
        {Icon && <Icon className="h-4 w-4 text-white" />}

        <input
          ref={inputTitleRef}
          className={`${editTitle ? '' : 'hidden'} flex min-h-5 items-center justify-start font-['Inter'] text-[8px] leading-tight font-semibold text-white`}
          type="text"
          value={textValue}
          onBlur={handleOnBlur}
          onChange={handleOnChange}
          onKeyUp={handleOnKeyUp}
        />
        {text && !editTitle && (
          <p className="max-w-[124px] truncate font-['Inter'] text-[8px] leading-tight font-semibold text-white">
            {text}
          </p>
        )}

        {switcher && (
          <div
            className={clsx(
              'relative flex items-center rounded bg-[#1F0D87] shadow-[inset_0px_0px_1.5px_0px_rgba(0,0,0,0.75)]',
            )}
          >
            <div
              className={clsx(
                'absolute top-0 z-20 h-full rounded bg-white outline outline-offset-[-1px] outline-white transition-all',
                isAnimated ? 'transition-all duration-300' : '',
              )}
              style={{
                width: highlightStyle.width,
                transform: `translateX(${highlightStyle.left}px)`,
              }}
            />

            {aligns.map((sz) => {
              const Icon =
                sz === 'vertical'
                  ? selectedSize === sz
                    ? IconArrowVerticalFilled
                    : IconArrowVertical
                  : selectedSize === sz
                    ? IconArrowHorizontalFilled
                    : IconArrowHorizontal;

              const iconClass = selectedSize === sz ? 'text-indigo-500' : 'text-indigo-300';

              return (
                <button
                  key={sz}
                  ref={(el) => {
                    optionRefs.current[sz] = el;
                  }}
                  onClick={() => handleClick(sz)}
                  className={clsx(
                    'relative z-20 flex h-5 w-5 cursor-pointer items-center justify-center',
                  )}
                  type="button"
                >
                  <Icon className={clsx(`${iconClass}`, 'h-3 w-3 transition-all')} />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

{
  /* {type === 'inactive' && (
        <div
          className={clsx(
            'inline-flex h-6 items-center justify-start gap-1 rounded-tl-md rounded-tr-md bg-indigo-500 px-1.5 py-[3px]',
            className,
          )}
        >
          <IconBorderAll className="text-white h-4 w-4" />
          {text && <p className="text-white text-[8px] leading-tight font-semibold">{text}</p>}
        </div>
      )} */
}
